import React from 'react';
import { Input, Tooltip } from 'antd';
import { MailOutlined, LockOutlined, MobileOutlined, UserOutlined, CloseOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { findComposeContact } from '../../utility/composeContactHelpers';
import '../../styles/compose/AccessCodeDeliveryModal.css';
import '../../styles/settings/profile.css';

const DEFAULT_METHOD = 'sms';

const AccessCodeDeliveryModal = ({
    open,
    onClose,
    onSave,
    recipients,
    accessCodeDelivery,
    onAccessCodeDeliveryChange,
    accessCodeSmsPhones,
    onAccessCodeSmsPhonesChange,
    contacts = [],
}) => {
    const { t } = useTranslation();

    const allRecipients = React.useMemo(() => {
        const list = [];
        (recipients.to || []).forEach((email) => {
            const e = typeof email === 'string' ? email.trim() : email;
            if (e) list.push({ email: e, type: 'to' });
        });
        (recipients.cc || []).forEach((email) => {
            const e = typeof email === 'string' ? email.trim() : email;
            if (e) list.push({ email: e, type: 'cc' });
        });
        (recipients.bcc || []).forEach((email) => {
            const e = typeof email === 'string' ? email.trim() : email;
            if (e) list.push({ email: e, type: 'bcc' });
        });
        return list;
    }, [recipients]);

    const setMethod = (email, method) => {
        onAccessCodeDeliveryChange((prev) => ({
            ...prev,
            [email]: method,
        }));
    };

    const setPhone = (email, value) => {
        onAccessCodeSmsPhonesChange((prev) => ({
            ...prev,
            [email]: value,
        }));
    };

    const handleFooterSave = async () => {
        if (onSave) {
            await onSave();
        } else {
            onClose();
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    if (!open) return null;

    return (
        <div className="profile-modal-overlay" onClick={handleOverlayClick}>
            <div className="profile-modal access-code-modal" onClick={(e) => e.stopPropagation()}>
                <div className="profile-modal-header">
                    <div className="profile-modal-icon lock">
                        <LockOutlined />
                    </div>
                    <h3>{t('compose.accessCodeDelivery.title', 'Share access code')}</h3>
                    <button className="access-code-modal-close" onClick={onClose} aria-label="Close">
                        <CloseOutlined />
                    </button>
                </div>

                <div className="profile-modal-content access-code-modal-body">
                    {allRecipients.length === 0 ? (
                        <p className="access-code-empty">
                            {t('compose.accessCodeDelivery.noRecipients', 'Add recipients to configure access code delivery.')}
                        </p>
                    ) : (
                        <div className="access-code-delivery-list">
                            {allRecipients.map(({ email }) => {
                                const currentMethod = accessCodeDelivery[email] ?? DEFAULT_METHOD;
                                const contact = findComposeContact(contacts, email);
                                return (
                                    <div key={email} className="access-code-delivery-row">
                                        <span className="access-code-delivery-email">
                                            {email}
                                            {contact && (
                                                <Tooltip title={t('compose.accessCodeDelivery.linkedContact', 'From your contacts')}>
                                                    <UserOutlined className="access-code-delivery-contact-icon" aria-hidden />
                                                </Tooltip>
                                            )}
                                        </span>
                                        <div className="access-code-delivery-controls">
                                            <div className="access-code-delivery-phone-wrap">
                                                {currentMethod === 'sms' ? (
                                                    <Input
                                                        className="access-code-delivery-phone"
                                                        value={accessCodeSmsPhones[email] || ''}
                                                        onChange={(e) => setPhone(email, e.target.value)}
                                                        maxLength={10}
                                                    />
                                                ) : (
                                                    <span className="access-code-email-warning">
                                                        <WarningOutlined />
                                                        {t('compose.accessCodeDelivery.emailWarning')}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="access-code-delivery-method">
                                                <Tooltip title={t('compose.accessCodeDelivery.sms', 'SMS')}>
                                                    <button
                                                        className={`access-code-method-btn${currentMethod === 'sms' ? ' active' : ''}`}
                                                        aria-label={t('compose.accessCodeDelivery.sms', 'SMS')}
                                                        aria-pressed={currentMethod === 'sms'}
                                                        onClick={() => setMethod(email, 'sms')}
                                                    >
                                                        <MobileOutlined />
                                                    </button>
                                                </Tooltip>
                                                <Tooltip title={t('compose.accessCodeDelivery.email', 'Email')}>
                                                    <button
                                                        className={`access-code-method-btn${currentMethod === 'email' ? ' active' : ''}`}
                                                        aria-label={t('compose.accessCodeDelivery.email', 'Email')}
                                                        aria-pressed={currentMethod === 'email'}
                                                        onClick={() => setMethod(email, 'email')}
                                                    >
                                                        <MailOutlined />
                                                    </button>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="profile-modal-actions">
                    <button className="profile-modal-cancel" onClick={onClose}>
                        {t('compose.accessCodeDelivery.cancel', 'Cancel')}
                    </button>
                    <button className="profile-modal-save" onClick={handleFooterSave}>
                        {t('compose.accessCodeDelivery.save', 'Save')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AccessCodeDeliveryModal;
